import { useState, useEffect, useMemo } from 'react';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import { useToast } from '../ui/Toast';

export default function SowDocumentDuplicatesPage() {
  const { toast } = useToast();
  const [groups, setGroups] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total }

  const load = () => {
    setGroups(null);
    api.get('sow-documents/duplicates')
      .then(d => {
        const gs = Array.isArray(d?.groups) ? d.groups : [];
        setGroups(gs);
        // Pre-select the likely-duplicate rows (everything after the first
        // upload of each identical-notes cluster) — reviewable before applying.
        setSelected(new Set(gs.flatMap(g => g.docs.filter(doc => doc.likely_duplicate).map(doc => doc.id))));
      })
      .catch(() => setGroups([]));
  };
  useEffect(load, []);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const totalDocs = useMemo(() => (groups || []).reduce((s, g) => s + g.docs.length, 0), [groups]);

  const deleteSelected = async () => {
    if (!selected.size) return;
    if (!confirm(`Delete ${selected.size} document link${selected.size === 1 ? '' : 's'}? This only removes the link — files already in storage aren't purged. This can't be undone from here.`)) return;
    setDeleting(true);
    const total = selected.size;
    let remaining = [...selected];
    let totalDeleted = 0;
    const allFailed = [];
    setProgress({ done: 0, total });
    try {
      // The backend caps each call — loop through in batches for large
      // cleanups (this can be hundreds/thousands of rows) rather than one
      // request that would time out.
      while (remaining.length) {
        const r = await api.post('sow-documents/duplicates/resolve', { delete_ids: remaining });
        totalDeleted += r.deleted || 0;
        if (r.failed?.length) allFailed.push(...r.failed);
        const processedCount = remaining.length - (r.remaining || 0);
        remaining = remaining.slice(processedCount);
        setProgress({ done: total - remaining.length, total });
      }
      toast[allFailed.length ? 'error' : 'success'](
        `Deleted ${totalDeleted} document${totalDeleted === 1 ? '' : 's'}${allFailed.length ? `, ${allFailed.length} failed (still referenced elsewhere — check manually)` : ''}.`
      );
      load();
    } catch (e) {
      toast.error(e.message || 'Failed to delete');
    } finally {
      setDeleting(false);
      setProgress(null);
    }
  };

  return (
    <div className="fade-in p-4">
      <div className="mb-4">
        <h1 className="text-base font-extrabold text-navy">SOW Document Duplicates</h1>
        <div className="text-[9px] text-gray-500 mt-0.5">
          Finds SOWs with more than one Statement of Work document attached — most commonly caused by uploading the
          same file twice (e.g. two browser tabs both running a bulk upload over the same folder). Rows flagged
          <strong> likely duplicate</strong> share identical upload notes with an earlier row for the same SOW, so
          they're pre-selected — review before deleting. Deleting only removes the document link from the SOW; the
          underlying file isn't purged from storage.
        </div>
      </div>

      {groups === null ? (
        <div className="text-center py-8 text-[10px] text-gray-400">Scanning…</div>
      ) : groups.length === 0 ? (
        <div className="text-center py-8 text-[10px] text-gray-400">No duplicate SOW documents found.</div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-3 bg-amber-50 border border-amber-200 rounded p-3">
            <div className="text-[9px] text-amber-800">
              <strong>{groups.length}</strong> SOW{groups.length === 1 ? '' : 's'} with duplicate documents
              (<strong>{totalDocs}</strong> documents total) · <strong>{selected.size}</strong> selected for deletion
            </div>
            <button onClick={deleteSelected} disabled={!selected.size || deleting}
              className="px-3 py-1.5 text-[10px] font-bold bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-40 flex-shrink-0">
              {deleting ? `Deleting… (${progress?.done ?? 0}/${progress?.total ?? selected.size})` : `Delete ${selected.size} selected`}
            </button>
          </div>

          <div className="space-y-3">
            {groups.map(g => (
              <div key={g.sow_id} className="bg-white border border-gray-200 rounded">
                <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
                  <span className="text-[10px] font-bold text-navy">{g.sow_reference}</span>
                  {g.sow_detail && <span className="text-[9px] text-gray-500 ml-2">{g.sow_detail}</span>}
                </div>
                <div className="overflow-x-auto"><table className="w-full">
                  <tbody>
                    {g.docs.map(doc => (
                      <tr key={doc.id} className={`border-t border-gray-100 ${doc.likely_duplicate ? 'bg-red-50/50' : ''}`}>
                        <td className="px-3 py-2 w-6">
                          <input type="checkbox" checked={selected.has(doc.id)} onChange={() => toggle(doc.id)} />
                        </td>
                        <td className="px-2 py-2">
                          <div className="text-[9px] font-semibold text-navy flex items-center gap-1.5">
                            {doc.title}
                            {doc.version && <span className="text-[7px] font-bold bg-navy/10 text-navy px-1 py-0.5 rounded">v{doc.version}</span>}
                            {doc.likely_duplicate && <span className="text-[7px] font-bold bg-red-100 text-red-600 px-1 py-0.5 rounded">LIKELY DUPLICATE</span>}
                          </div>
                          {doc.notes && <div className="text-[8px] text-gray-500 mt-0.5">{doc.notes}</div>}
                          <div className="text-[8px] text-gray-400 mt-0.5">
                            {doc.created_at ? formatDate(doc.created_at) : '—'}{doc.created_by_name ? ` · ${doc.created_by_name}` : ''}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right w-16">
                          {doc.url && <a href={doc.url} target="_blank" rel="noreferrer" className="text-[8px] font-semibold text-blue-600 hover:underline">Open ↗</a>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
